import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import {
  Shield, Truck, CheckCircle, ArrowRight,
  MapPin, ChevronRight, Star, Eye, RefreshCw,
  Zap, Lock, MessageSquare, HelpCircle
} from 'lucide-react';
import { useState } from 'react';

const HERO_PRIMARY_IMG = '/images/Hero-img.png';
const HERO_LIGHT_IMG = '/images/Hero-img-light.png';
const HERO_FALLBACK_IMG = '/images/Hero-img2.png';

function HeroQuoteCard({ theme }) {
  return (
    <div className="hidden lg:block w-full max-w-sm" data-testid="hero-quote-card">
      <div className={`hero-quote-card rounded-2xl p-6 opacity-0 animate-fade-in stagger-4 ${
        theme === 'light'
          ? 'bg-gray-800/90 border border-gray-700 shadow-lg backdrop-blur-sm'
          : ''
      }`}>
        <p className={`text-[10px] uppercase tracking-[0.25em] mb-5 font-mono ${
          theme === 'light' ? 'text-gray-300' : 'text-white/40'
        }`}>Instant Quote Preview</p>
        <div className="space-y-3">
          <div className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
            theme === 'light'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/[0.04] border-white/[0.06]'
          }`}>
            <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
            <div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Pickup</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Los Angeles, CA 90001</p>
            </div>
          </div>
          <div className={`flex items-center gap-3 rounded-lg px-4 py-3 border ${
            theme === 'light'
              ? 'bg-white/10 border-white/20'
              : 'bg-white/[0.04] border-white/[0.06]'
          }`}>
            <MapPin className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
            <div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Delivery</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>New York, NY 10001</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={`rounded-lg px-4 py-3 border ${
              theme === 'light'
                ? 'bg-white/10 border-white/20'
                : 'bg-white/[0.04] border-white/[0.06]'
            }`}>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Model</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Model Y</p>
            </div>
            <div className={`rounded-lg px-4 py-3 border ${
              theme === 'light'
                ? 'bg-white/10 border-white/20'
                : 'bg-white/[0.04] border-white/[0.06]'
            }`}>
              <p className={`text-[10px] ${theme === 'light' ? 'text-gray-300' : 'text-white/30'}`}>Transport</p>
              <p className={`text-sm font-medium ${theme === 'light' ? 'text-white' : 'text-white/90'}`}>Enclosed</p>
            </div>
          </div>
          <div className={`border-t pt-4 mt-1 ${theme === 'light' ? 'border-white/20' : 'border-white/[0.06]'}`}>
            <div className="flex items-end justify-between mb-4">
              <div>
                <p className={`text-[10px] ${theme === 'light' ? 'text-white' : 'text-white/30'}`}>Estimated Price</p>
                <p className={`text-3xl font-outfit font-bold text-white`}>$1,450</p>
              </div>
              <p className={`text-[10px] ${theme === 'light' ? 'text-white' : 'text-white/30'}`}>~2,800 mi</p>
            </div>
            <Link to="/quote">
              <Button
                className="w-full bg-accent text-white hover:bg-accent/90 rounded-xl py-5 text-sm font-semibold transition-all duration-300 hover:shadow-[0_0_20px_rgba(227,25,55,0.3)]"
                data-testid="hero-card-reserve-btn"
              >
                Reserve Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [imgLoaded, setImgLoaded] = useState(false);
  const { theme } = useTheme();

  const trustBullets = [
    { icon: Shield,    text: 'Verified & insured carriers' },
    { icon: Lock,      text: 'Enclosed and open options' },
    { icon: Eye,       text: 'Real-time status updates' },
    { icon: RefreshCw, text: 'Refundable deposit before dispatch' },
  ];

  const howItWorks = [
    { step: '01', title: 'Get a quote in minutes',           description: 'Enter your vehicle, route, and transport preference for an instant price.',        icon: Zap  },
    { step: '02', title: 'Reserve with a refundable deposit', description: 'Your deposit is fully refundable until dispatch confirmation.',                      icon: Lock },
    { step: '03', title: 'Track pickup to delivery',          description: 'Receive updates from carrier assignment through delivery completion.',               icon: Eye  },
  ];

  const whyShipTesla = [
    { icon: CheckCircle,   title: 'Verified carrier network',  description: 'Every carrier is licensed, insured, and vetted to our standards.'                       },
    { icon: Zap,           title: 'EV-aware handling',         description: 'Soft straps, careful loading, and EV-specific protocols for your Tesla.'               },
    { icon: Shield,        title: 'Transparent pricing',       description: 'Margin-protected dispatch means fair pricing for you and reliable service.'             },
    { icon: MessageSquare, title: 'Proactive updates',         description: 'SMS and email updates from booking to delivery. No guessing.'                          },
  ];

  const faqPreview = [
    { q: 'How is pricing calculated?',             a: 'Based on distance and transport type. Open carrier: $1.05/mi + $250. Enclosed: $1.68/mi + $350. Short-distance minimums apply.' },
    { q: 'Is the deposit refundable?',             a: 'Yes. Your deposit is fully refundable until dispatch confirmation. After a carrier is assigned, the deposit becomes non-refundable.' },
    { q: 'Open vs enclosed — what should I choose?', a: 'Open is cost-effective and widely available. Enclosed provides maximum protection from weather and road debris — recommended for high-value Teslas.' },
    { q: 'What does "pickup window" mean?',        a: "The pickup window is the range of dates you're available for carrier pickup. A minimum 72-hour window is required. Exact times are not guaranteed." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* ── Hero Section (always dark — sits on image + overlay) ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-zoom">
          <img
            src={theme === 'light' ? HERO_LIGHT_IMG : HERO_PRIMARY_IMG}
            alt="Enclosed auto transport carrier loading a Tesla at sunset"
            className={`w-full h-full object-cover object-[center_60%] sm:object-center transition-opacity duration-1000 hero-image-enhance ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setImgLoaded(true)}
            loading="eager"
            data-testid="hero-image"
          />
        </div>

        {!imgLoaded && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${theme === 'light' ? HERO_LIGHT_IMG : HERO_FALLBACK_IMG}')`, filter: 'blur(4px) brightness(0.3)' }}
          />
        )}

        <div className={`absolute inset-0 ${theme === 'light' ? 'bg-white/[0.35]' : 'bg-black/[0.60]'}`} />
        <div className={`absolute inset-0 bg-gradient-to-t ${theme === 'light' ? 'from-white via-white/10 to-transparent' : 'from-[#030303] via-[#030303]/25 to-transparent'}`} />
        <div className={`absolute inset-0 bg-gradient-to-r ${theme === 'light' ? 'from-white/20 via-transparent to-transparent' : 'from-[#030303]/45 via-transparent to-transparent'}`} />
        <div className="absolute inset-0 hero-grain pointer-events-none" />
        <div className="absolute inset-0 hero-light-sweep pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="flex items-center gap-12 lg:gap-20">
            <div className="flex-1 max-w-xl">
              <p className={`text-[11px] uppercase tracking-[0.3em] font-mono mb-6 opacity-0 animate-fade-in ${
                theme === 'light' ? 'text-gray-800' : 'text-white/40'
              }`}>
                Nationwide Enclosed &amp; Open Tesla Transport
              </p>
              <h1 className={`font-outfit text-5xl sm:text-6xl lg:text-7xl font-bold tracking-[-0.02em] leading-[1.05] mb-6 opacity-0 animate-fade-in stagger-1 ${
                theme === 'light' ? 'text-gray-900' : 'text-white'
              }`}>
                Premium Tesla
                <span className="block hero-accent-text">Transport</span>
              </h1>
              <p className={`text-base lg:text-lg mb-10 opacity-0 animate-fade-in stagger-2 leading-relaxed max-w-md ${
                theme === 'light' ? 'text-gray-800' : 'text-white/50'
              }`}>
                Verified carriers. Enclosed or open. Clear pricing. Updates from pickup to delivery.
              </p>
              <ul className="space-y-3.5 mb-12">
                {trustBullets.map((item, i) => (
                  <li
                    key={i}
                    className={`flex items-center gap-3 text-[13px] opacity-0 animate-fade-in stagger-${i + 3} ${
                      theme === 'light' ? 'text-gray-800' : 'text-white/60'
                    }`}
                    data-testid={`trust-bullet-${i}`}
                  >
                    <item.icon className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
                    {item.text}
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in stagger-7">
                <Link to="/quote">
                  <Button
                    className={`rounded-full px-10 py-6 text-sm font-semibold transition-all duration-300 ${
                      theme === 'light'
                        ? 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.97]'
                        : 'bg-white text-black hover:bg-white/90'
                    } hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(255,255,255,0.15)]`}
                    data-testid="hero-get-quote-btn"
                  >
                    Get Instant Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a href="#how-it-works">
                  <Button
                    variant="outline"
                    className={`rounded-full px-10 py-6 text-sm transition-all duration-300 ${
                      theme === 'light'
                        ? 'border-gray-800 text-gray-900 hover:bg-gray-900 hover:text-white'
                        : 'border-white/15 text-white/70 hover:bg-white/5 hover:text-white'
                    }`}
                    data-testid="hero-how-it-works-btn"
                  >
                    How It Works
                  </Button>
                </a>
              </div>
            </div>
            <HeroQuoteCard theme={theme} />
          </div>
        </div>
      </section>

      {/* ── Social Proof Strip ── */}
      <section className="py-5 border-b border-border" data-testid="social-proof-strip">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-[13px] text-muted-foreground">
            <span className="font-medium">Trusted by Tesla owners nationwide</span>
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-accent text-accent" />
              ))}
              <span className="ml-2 font-semibold text-foreground">4.9/5</span>
              <span className="ml-1">average rating</span>
            </div>
            <span className="font-medium">1,200+ Teslas transported</span>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-28" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="overline mb-4">Simple Process</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              How It Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorks.map((item, i) => (
              <div key={i} className="relative group" data-testid={`how-step-${i}`}>
                <div className="bg-card border border-border group-hover:border-border/70 rounded-2xl p-8 transition-all duration-500 h-full shadow-sm">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
                    </div>
                    <span className="font-mono text-[11px] text-muted-foreground/50">{item.step}</span>
                  </div>
                  <h3 className="font-outfit text-lg font-semibold mb-2 text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Open vs Enclosed ── */}
      <section className="py-28 bg-muted/30" data-testid="transport-comparison-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="overline mb-4">Choose Your Transport</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Open vs Enclosed
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Open */}
            <div className="bg-card border border-border rounded-2xl p-8 hover:border-accent/20 transition-all duration-500 shadow-sm" data-testid="transport-open-card">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                  <Truck className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-foreground">Open Carrier</h3>
              </div>
              <p className="text-accent text-sm font-medium mb-2">Best value, fastest availability</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Multi-car open trailer — the most popular and cost-effective option for vehicle transport nationwide.
              </p>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> From $1.05/mile + $250</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> $199 refundable deposit</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Full insurance coverage</li>
              </ul>
            </div>

            {/* Enclosed */}
            <div className="bg-card border border-accent/20 rounded-2xl p-8 hover:border-accent/40 transition-all duration-500 relative shadow-sm" data-testid="transport-enclosed-card">
              <div className="absolute -top-3 right-6">
                <span className="bg-accent text-white text-[10px] font-semibold px-3 py-1 rounded-full shadow-[0_0_12px_rgba(227,25,55,0.3)]">
                  Recommended for high-value Teslas
                </span>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-foreground">Enclosed Carrier</h3>
              </div>
              <p className="text-accent text-sm font-medium mb-2">Maximum protection for premium vehicles</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Fully enclosed trailer protects against weather, road debris, and provides total security during transit.
              </p>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> From $1.68/mile + $350</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> $299 refundable deposit</li>
                <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-accent shrink-0" /> Premium insurance coverage</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why ShipTesla ── */}
      <section className="py-28" data-testid="why-shiptesla-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="overline mb-4">The Difference</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Why ShipTesla
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyShipTesla.map((item, i) => (
              <div
                key={i}
                className="bg-card border border-border hover:border-accent/20 rounded-2xl p-6 transition-all duration-500 shadow-sm"
                data-testid={`why-card-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-accent" strokeWidth={1.5} />
                </div>
                <h3 className="font-outfit text-base font-semibold mb-2 text-foreground">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ Preview ── */}
      <section className="py-28 bg-muted/30" data-testid="faq-preview-section">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-20">
            <p className="overline mb-4">Common Questions</p>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              FAQ
            </h2>
          </div>

          <div className="space-y-3">
            {faqPreview.map((item, i) => (
              <FaqItem key={i} question={item.q} answer={item.a} index={i} />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/faq">
              <Button
                variant="outline"
                className="rounded-full border-border hover:bg-muted px-8 text-muted-foreground hover:text-foreground transition-all"
                data-testid="faq-view-all-btn"
              >
                View all FAQs
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-28" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl md:text-5xl font-bold tracking-tight mb-5 text-foreground">
            Ready to ship your Tesla?
          </h2>
          <p className="text-base text-muted-foreground mb-12 max-w-lg mx-auto">
            Get an instant quote in under 60 seconds. No obligation, transparent pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/quote">
              <Button
                className="bg-accent text-white hover:bg-accent/90 rounded-full px-12 py-6 text-sm font-semibold transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(227,25,55,0.25)]"
                data-testid="final-cta-btn"
              >
                Get Instant Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Contact support
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden transition-all duration-300 hover:border-border/70 shadow-sm"
      data-testid={`faq-item-${index}`}
    >
      <button
        className="w-full flex items-center justify-between px-6 py-5 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="font-medium text-sm pr-4 text-foreground">{question}</span>
        <HelpCircle className={`h-4 w-4 text-muted-foreground/50 shrink-0 transition-transform duration-300 ${open ? 'rotate-45 text-accent' : ''}`} strokeWidth={1.5} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40' : 'max-h-0'}`}>
        <div className="px-6 pb-5">
          <p className="text-sm text-muted-foreground leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}
